import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingModal = ({ event, onClose }) => {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", seats: 1, message: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (event) setForm({ name: "", email: "", seats: 1, message: "" });
  }, [event]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const maxSeats = event && event.capacity > 0 ? Math.max(1, event.capacity - (event.booked || 0)) : 20;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/bookings`, {
        event_id: event.id,
        name: form.name,
        email: form.email,
        seats: Number(form.seats),
        message: form.message,
      });
      toast.success(t.events.booking_success);
      onClose();
    } catch (err) {
      toast.error(err.response?.status === 409 ? t.events.booking_full : t.events.booking_error);
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "mt-2 w-full border border-gold/20 bg-ink px-4 py-3 text-sm text-parchment outline-none transition-colors focus:border-gold";
  const labelCls = "mt-5 block font-mono text-[10px] uppercase tracking-[0.25em] text-sand";

  return (
    <AnimatePresence>
      {event && (
        <motion.div
          data-testid="booking-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-ink/92 p-4 backdrop-blur-lg sm:p-10"
        >
          <motion.form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            initial={{ scale: 0.96, opacity: 0, y: 16 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-lg border border-gold/25 bg-ink2 p-8 sm:p-10"
          >
            <button
              type="button"
              data-testid="booking-modal-close"
              aria-label={t.events.booking_close}
              onClick={onClose}
              className="absolute right-4 top-4 rounded-full border border-gold/40 p-2 text-parchment transition-colors hover:bg-gold hover:text-ink"
            >
              <X size={16} />
            </button>
            <p className="font-mono text-[11px] uppercase tracking-[0.35em] text-gold">{t.events.book}</p>
            <h3 className="mt-3 font-serif text-2xl font-light leading-tight text-parchment sm:text-3xl">
              {lang === "fr" ? event.title_fr : event.title_en}
            </h3>
            <p className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-sand">
              <MapPin size={12} className="text-gold" />
              {lang === "fr" ? event.date_label_fr : event.date_label_en} · {lang === "fr" ? event.location_fr : event.location_en}
            </p>
            <p className="mt-4 text-sm font-light leading-relaxed text-sand">{t.events.booking_intro}</p>

            <label className={labelCls}>{t.events.booking_name}</label>
            <input data-testid="booking-name-input" required maxLength={120} value={form.name} onChange={set("name")} className={inputCls} />
            <label className={labelCls}>{t.events.booking_email}</label>
            <input data-testid="booking-email-input" type="email" required value={form.email} onChange={set("email")} className={inputCls} />
            <label className={labelCls}>{t.events.booking_seats}</label>
            <input data-testid="booking-seats-input" type="number" min={1} max={maxSeats} required value={form.seats} onChange={set("seats")} className={inputCls} />
            <label className={labelCls}>{t.events.booking_message}</label>
            <textarea data-testid="booking-message-input" rows={3} maxLength={2000} value={form.message} onChange={set("message")} className={inputCls} />

            <button
              data-testid="booking-submit-button"
              type="submit"
              disabled={busy}
              className="mt-8 w-full rounded-full border border-gold/60 px-8 py-3.5 font-mono text-[11px] uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-50"
            >
              {busy ? t.events.booking_sending : t.events.booking_send}
            </button>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BookingModal;
